import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  type OnInit,
  Output,
} from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { type BUIDMid, MerchantService } from '@odin/odin-core';
import type { ITransaction } from '../../models';

/**
 * A MonekID (merchant account) the operator can choose to process a refund
 * or recharge against. Built from the business unit's MIDs in the user profile.
 */
export interface AccountOption {
  monekId: string;
  acquirer: string;
  merchantId: string;
  isPrimary: boolean;
  /** The account the original transaction was taken on. */
  isOriginal: boolean;
  isActive: boolean;
  isTestMid: boolean;
}

/**
 * Lets the operator pick which MonekID on the current business unit an action
 * should be processed against.
 *
 * Simple customers (one eligible account) see a read-only summary row so there
 * is nothing to decide. Advanced customers (several accounts) get a dropdown
 * that defaults to the account the original transaction was taken on.
 */
@Component({
  standalone: false,
  selector: 'odin-account-selector',
  templateUrl: './account-selector.component.html',
  styleUrls: ['./account-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSelectorComponent implements OnInit {
  @Input({ required: true }) transaction!: ITransaction;
  /** Label shown above the control, e.g. "Refund from" / "Charge to". */
  @Input() label = 'Account';
  /** Prefix for data-test selectors, e.g. "tx-refund". */
  @Input() testPrefix = 'tx-account';

  @Output() selectionChange = new EventEmitter<AccountOption>();

  public options: AccountOption[] = [];
  public selected: AccountOption | null = null;

  constructor(private merchantService: MerchantService) {}

  ngOnInit(): void {
    this.options = this.buildOptions();
    this.selected = this.pickDefault(this.options);
    if (this.selected) this.selectionChange.emit(this.selected);
  }

  /** True when there is a genuine choice to make. */
  public get hasChoice(): boolean {
    return this.options.length > 1;
  }

  public onSelect(change: MatSelectChange | string): void {
    const monekId = typeof change === 'string' ? change : change.value;
    const match = this.options.find((o) => o.monekId === monekId) ?? null;
    if (!match) return;
    this.selected = match;
    this.selectionChange.emit(match);
  }

  private buildOptions(): AccountOption[] {
    const originalMonekId = this.transaction?.merchant?.monekId ?? '';
    const currency = this.transaction?.currencyCode ?? '';
    const mids: BUIDMid[] = this.merchantService.getCurrentBuid()?.mids ?? [];

    const options = mids
      .filter((mid) => !!mid?.monekId)
      .filter(
        (mid) =>
          this.supportsCurrency(mid, currency) ||
          mid.monekId === originalMonekId,
      )
      .map<AccountOption>((mid) => ({
        monekId: mid.monekId,
        acquirer: mid.acquirer ?? '',
        merchantId: mid.merchantId ?? '',
        isPrimary: !!mid.isPrimary,
        isOriginal: mid.monekId === originalMonekId,
        isActive: (mid.status ?? '').toUpperCase() === 'ACTIVE',
        isTestMid: !!mid.isTestMid,
      }));

    // The transaction's own account is always offered, even if the profile
    // doesn't list it (e.g. it has since been closed or the profile is stale).
    if (originalMonekId && !options.some((o) => o.isOriginal)) {
      options.unshift({
        monekId: originalMonekId,
        acquirer: this.transaction.merchant?.acquirer ?? '',
        merchantId: this.transaction.merchant?.merchantNumber ?? '',
        isPrimary: false,
        isOriginal: true,
        isActive: true,
        isTestMid: false,
      });
    }

    // Original first, then primary, then the rest by MonekID.
    return options.sort((a, b) => {
      if (a.isOriginal !== b.isOriginal) return a.isOriginal ? -1 : 1;
      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
      return a.monekId.localeCompare(b.monekId);
    });
  }

  private pickDefault(options: AccountOption[]): AccountOption | null {
    return (
      options.find((o) => o.isOriginal && o.isActive) ??
      options.find((o) => o.isPrimary && o.isActive) ??
      options.find((o) => o.isActive) ??
      options[0] ??
      null
    );
  }

  private supportsCurrency(mid: BUIDMid, currency: string): boolean {
    if (!currency) return true;
    const codes = mid.currencyCodes ?? [];
    if (codes.length === 0) return true;
    return codes.includes(currency);
  }
}
